import { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Checkbox,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { AttachmentDto, Currency } from "../../api/types";
import { CurrencySchema } from "../../api/types";
import {
  computeAutofillChanges,
  dottedDateToIso,
  suggestedSender,
  type AutofillApply,
  type AutofillChange,
  type AutofillCurrent,
  type AutofillFieldKey,
} from "../../logic/invoice-autofill-diff";
import type { DetectionResult } from "../../logic/invoice-detection";
import { useRegistry } from "../../context/RegistryContext";
import AttachmentPreview from "../AttachmentPreview";
import { AttachmentPreviewInline } from "../AttachmentPreviewInline";
import type { InvoiceSenderRef } from "../../logic/invoice-sender";
import { DatePicker } from "../DatePicker";
import { InvoiceSenderPicker } from "./InvoiceSenderPicker";
import { MissingSenderAlert } from "./MissingSenderAlert";

export interface InvoiceAutofillReviewDialogProps {
  open: boolean;
  onClose: () => void;
  result: DetectionResult;
  current: AutofillCurrent;
  /** The PDF the suggestion came from, shown as the receipt to double-check. */
  attachment: AttachmentDto | undefined;
  onApply: (applied: AutofillApply) => void;
}

/**
 * Reviews an auto-fill suggestion before it touches the invoice form. Each
 * differing field is a row the user can accept/reject and edit; a receipt pane
 * lets them verify against the PDF. Used for both new and edit invoices — the
 * add-vs-change split falls out of the diff.
 */
export const InvoiceAutofillReviewDialog = ({
  open,
  onClose,
  result,
  current,
  attachment,
  onApply,
}: InvoiceAutofillReviewDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isWide = useMediaQuery(theme.breakpoints.up("md"));
  const { getPayee, getSource } = useRegistry();

  const { suggestion, suggestedFilename } = result;

  const changes = useMemo(
    () => computeAutofillChanges(suggestion, suggestedFilename, current),
    [suggestion, suggestedFilename, current],
  );

  // Editable proposed values, seeded from the suggestion.
  const [name, setName] = useState(suggestion.name);
  // The sender keeps the kind the detector declared — a vendor's bill
  // proposes a payee, a card statement an account — and is pre-picked when
  // that registry already has the name.
  const detectedSender = suggestedSender(suggestion);
  const [sender, setSender] = useState<InvoiceSenderRef | null>(() => {
    if (detectedSender.kind === "payee") {
      const payee = getPayee(detectedSender.name);
      return payee ? { kind: "payee", id: payee.id } : null;
    }
    const source = getSource(detectedSender.name);
    return source ? { kind: "source", id: source.id } : null;
  });
  const [amount, setAmount] = useState(suggestion.amount?.amount ?? "");
  const [amountMax, setAmountMax] = useState(
    suggestion.amountMax?.amount ?? "",
  );
  const [currency, setCurrency] = useState<Currency>(
    (suggestion.amount?.currency ??
      suggestion.amountMax?.currency ??
      "NOK") as Currency,
  );
  const [dateReceived, setDateReceived] = useState(
    dottedDateToIso(suggestion.dateReceived),
  );
  const [dueDate, setDueDate] = useState(dottedDateToIso(suggestion.dueDate));
  const [filename, setFilename] = useState(suggestedFilename);
  const [kid, setKid] = useState(suggestion.kid ?? "");
  const [payToAccount, setPayToAccount] = useState(
    suggestion.payToAccount ?? "",
  );

  const [included, setIncluded] = useState<Set<AutofillFieldKey>>(
    () => new Set(changes.map((c) => c.key)),
  );
  const toggle = (key: AutofillFieldKey) =>
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const senderUnresolved =
    included.has("sender") &&
    changes.some((c) => c.key === "sender") &&
    !sender;

  const handleApply = () => {
    const applied: AutofillApply = {};
    if (included.has("name")) applied.name = name;
    if (included.has("sender") && sender) {
      if (sender.kind === "payee") applied.payeeId = sender.id;
      else applied.sourceId = sender.id;
    }
    if (included.has("amount")) {
      applied.amount = amount;
      applied.currency = currency;
    }
    if (included.has("amountMax")) {
      applied.amountMax = amountMax;
      applied.currency = currency;
    }
    if (included.has("kid")) applied.kid = kid;
    if (included.has("payToAccount")) applied.payToAccount = payToAccount;
    if (included.has("dateReceived")) applied.dateReceived = dateReceived;
    if (included.has("dueDate")) applied.dueDate = dueDate;
    if (included.has("filename")) applied.filename = filename;
    onApply(applied);
  };

  const renderControl = (key: AutofillFieldKey, disabled: boolean) => {
    switch (key) {
      case "name":
        return (
          <TextField
            size="small"
            fullWidth
            value={name}
            disabled={disabled}
            onChange={(e) => setName(e.target.value)}
          />
        );
      case "filename":
        return (
          <TextField
            size="small"
            fullWidth
            value={filename}
            disabled={disabled}
            onChange={(e) => setFilename(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        );
      case "kid":
        return (
          <TextField
            size="small"
            fullWidth
            value={kid}
            disabled={disabled}
            onChange={(e) => setKid(e.target.value)}
          />
        );
      case "payToAccount":
        return (
          <TextField
            size="small"
            fullWidth
            value={payToAccount}
            disabled={disabled}
            onChange={(e) => setPayToAccount(e.target.value)}
          />
        );
      case "amount":
        return (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="number"
              value={amount}
              disabled={disabled}
              onChange={(e) => setAmount(e.target.value)}
              slotProps={{ htmlInput: { step: "0.01" } }}
            />
            <FormControl size="small" sx={{ minWidth: 96 }} disabled={disabled}>
              <InputLabel id="review-currency">Currency</InputLabel>
              <Select
                labelId="review-currency"
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
              >
                {CurrencySchema.options.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        );
      case "amountMax":
        // Currency is shared with (and edited on) the Amount row.
        return (
          <TextField
            size="small"
            type="number"
            fullWidth
            value={amountMax}
            disabled={disabled}
            onChange={(e) => setAmountMax(e.target.value)}
            slotProps={{ htmlInput: { step: "0.01" } }}
          />
        );
      case "dateReceived":
        return (
          <DatePicker
            mode="single"
            fullWidth
            value={dateReceived || null}
            onChange={(v) => setDateReceived(v ?? "")}
          />
        );
      case "dueDate":
        return (
          <DatePicker
            mode="single"
            fullWidth
            value={dueDate || null}
            onChange={(v) => setDueDate(v ?? "")}
          />
        );
      case "sender":
        return (
          <InvoiceSenderPicker
            kind={detectedSender.kind}
            value={sender?.id ?? null}
            onChange={setSender}
          />
        );
    }
  };

  const renderRow = (change: AutofillChange) => {
    const isIncluded = included.has(change.key);
    return (
      <Box
        key={change.key}
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          opacity: isIncluded ? 1 : 0.5,
          py: 1,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Checkbox
          size="small"
          checked={isIncluded}
          onChange={() => toggle(change.key)}
          sx={{ mt: 0.5 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 0.5, alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {change.label}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={change.kind === "add" ? "Add" : "Change"}
              color={change.kind === "add" ? "success" : "info"}
            />
          </Stack>
          {change.kind === "change" && (
            <Typography
              variant="body2"
              sx={{
                textDecoration: "line-through",
                color: "error.main",
                mb: 0.5,
              }}
            >
              {change.currentText}
            </Typography>
          )}
          {renderControl(change.key, !isIncluded)}
        </Box>
      </Box>
    );
  };

  // The detector's categorization for the matched bank transaction. It is
  // carried outside this review on purpose — it feeds the invoice's virtual
  // auto-transaction rule and survives even a cancelled review (see
  // `matchSnapshot` in InvoiceFormDialog) — so it is shown read-only rather
  // than as a row that pretends to be optional.
  const transactionInfo = suggestion.transactionInfo;
  const categorizationNote = transactionInfo && (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        Categorization of the matched transaction
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {/* Keyed by kind as well as label: a group and a category may share
            a name (Telia files under "Kommunikasjon" for both). */}
        {[
          { kind: "expenseType", label: transactionInfo.expenseType },
          { kind: "group", label: transactionInfo.group },
          ...transactionInfo.categories.map((label) => ({
            kind: "category",
            label,
          })),
        ]
          .filter(
            (c): c is { kind: string; label: string } =>
              c.label != null && c.label !== "",
          )
          .map((c) => (
            <Chip
              key={`${c.kind}:${c.label}`}
              size="small"
              variant="outlined"
              label={c.label}
            />
          ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Applied when a bank transaction matches this invoice.
      </Typography>
    </Box>
  );

  const changeList =
    changes.length === 0 ? (
      <Stack>
        <Typography sx={{ color: "text.secondary" }}>
          Everything already matches the PDF — nothing to apply.
        </Typography>
        {categorizationNote}
      </Stack>
    ) : (
      <Stack>
        {changes.map(renderRow)}
        {categorizationNote}
        {senderUnresolved && (
          <MissingSenderAlert
            kind={detectedSender.kind}
            name={detectedSender.name}
            onAdded={setSender}
          />
        )}
      </Stack>
    );

  // Only the wide split layout has room to keep the receipt on screen next to
  // the changes.
  const receiptPane = attachment && (
    <Box sx={{ minHeight: 0 }}>
      <Typography variant="caption" color="text.secondary">
        Receipt — pinch/scroll to zoom
      </Typography>
      <AttachmentPreviewInline attachment={attachment} />
    </Box>
  );

  // Narrow layouts get a tap-to-open thumbnail instead: a full-page PDF inline
  // pushes every change below the fold, which is the one thing this dialog is
  // for. Same affordance the invoice form uses when it can't split.
  const receiptThumbnail = attachment && (
    <Paper
      variant="outlined"
      sx={{ display: "flex", alignItems: "center", p: 1 }}
    >
      <AttachmentPreview attachment={attachment} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Receipt
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", wordBreak: "break-all" }}
        >
          Tap to check {attachment.filename}
        </Typography>
      </Box>
    </Paper>
  );

  const showSplit = isWide && !!attachment;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={showSplit ? "lg" : "sm"}
      fullWidth
    >
      <DialogTitle>Review auto-fill — {result.detectorLabel}</DialogTitle>
      <DialogContent dividers>
        {showSplit ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              alignItems: "stretch",
              minHeight: 460,
            }}
          >
            <Box sx={{ minHeight: 0 }}>{receiptPane}</Box>
            <Box>{changeList}</Box>
          </Box>
        ) : (
          <Stack spacing={2}>
            {receiptThumbnail}
            {changeList}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={
            included.size === 0 || changes.length === 0 || senderUnresolved
          }
        >
          Apply {included.size} change{included.size === 1 ? "" : "s"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
